import { FileSharingService } from '../Api/Api';
import {
  getSelectedFolder$,
  selectedFolderIdSubject$,
  setSelectedFolder$,
} from '../Observables/SelectedFolder.observable';
import { setIsServerError } from './ServerError.service';
import { lastValueFrom, take } from 'rxjs';
import {
  getSearchStatus$,
  setSearchStatus$,
  getSearchSubject$,
  setSearchSubject$,
  getDisableButtonsStatus$,
  setDisableButtonsStatus$,
} from '../Observables/Search.observable';
import { setIsLoading } from './Loading.service';
import {
  authorizeActions,
  getSelectedFolderId,
  setSelectedFolder,
  setSelectedFolderId,
} from './SelectedFolder.service';
import { getSort } from './Sort.service';
import biService from '../BiEvents/BiService';
import { getBreadCrumbs$ } from '../Observables/BreadCrumbs.observable';
import { BI_FILE_SHARE_WIDGET_SEARCH } from '../BiEvents/Constants/BiConstants';
import { setBreadCrumbs } from './BreadCrumbs.service';
import { isAbortError, throwIfAborted } from './Constants/AbortError.Constants';

const API = new FileSharingService();

let inFlightSearchController: AbortController | undefined;

export const abortInFlightSearch = () => {
  if (inFlightSearchController) {
    inFlightSearchController.abort();
    inFlightSearchController = undefined;
  }
};

export const setSearchSubject = (searchSubject: any) => {
  setSearchSubject$(searchSubject);
};

export const getSearchSubject = () => {
  return getSearchSubject$();
};
export const setSearchStatus = (searchStatus: any) => {
  setSearchStatus$(searchStatus);
};

export const getSearchStatus = () => {
  return getSearchStatus$();
};

export const setDisableButtonsStatus = (disableButtonsStatus: boolean) => {
  setDisableButtonsStatus$(disableButtonsStatus);
};

export const getDisableButtonsStatus = () => {
  return getDisableButtonsStatus$();
};

export const searchService = async () => {
  let results_count = 0;
  let folder_name = '';
  // Register the controller before any await so a superseding search can
  // always abort this one, even while it is still in the early-await window.
  const controller = new AbortController();
  inFlightSearchController = controller;
  const { signal } = controller;
  const clearController = () => {
    if (inFlightSearchController === controller) {
      inFlightSearchController = undefined;
    }
  };
  const selectedFolder = await getSelectedFolder();
  if (selectedFolder === undefined) {
    clearController();
    return;
  }
  setIsLoading(true);
  setSearchStatus(undefined);
  const selectedFolderId = await getCurrentSelectedId();
  const searchValue = await getSearchValue();
  const sortValue = await getSortValue();
  const breadCrumbs = await getBreadCrumbsValue();
  if (!searchValue.callApi) {
    if (searchValue.value === '') {
      setSearchStatus(false);
    } else {
      setSearchStatus(true);
    }
    setIsLoading(false);
    clearController();
    return;
  }
  let result: string | undefined;
  try {
    const query: any = {
      sort: {
        sortBy: sortValue.sortBy,
        orientation: sortValue.orientation,
      },
    };

    if (searchValue && searchValue.value !== '') {
      query.filter = {
        descendantsOfFolderId: selectedFolderId,
        search: searchValue.value,
      };
    } else {
      query.filter = {
        parentLibraryItemIds: [selectedFolderId],
      };
    }

    const res = await API.searchByValue(query, signal);
    throwIfAborted(signal);

    if (!res?.data) {
      // searchByValue resolves with the error object (instead of throwing)
      // on a non-abort failure, so a missing `data` means the request failed.
      setIsServerError({
        isError: true,
        message: res?.message,
      });
      setIsLoading(false);
      result = res?.message;
      return result;
    }

    if (res?.data?.libraryItems && res?.data?.libraryItems?.length > 0) {
      results_count = res?.data?.libraryItems?.length;
      const authorizedFolders = await authorizeActions(
        res.data,
        res.data.libraryItems[0].parentFolderId,
        signal,
      );
      throwIfAborted(signal);
      if (authorizedFolders !== undefined && authorizedFolders?.libraryItems) {
        const data = {
          libraryItems: authorizedFolders.libraryItems,
          folderAuthorizedActions: authorizedFolders.folderAuthorizedActions,
          metaData: res.data.metaData,
        };
        setSelectedFolder$(data);
      }
    } else {
      const selectedFolderLastValue = await getSelectedFolder();
      setSelectedFolder$({
        ...selectedFolderLastValue,
        ...res.data,
      });
    }
    setSearchStatus(!!searchValue?.value);
    setIsLoading(false);
    folder_name =
      breadCrumbs && breadCrumbs?.length > 0
        ? breadCrumbs[breadCrumbs?.length - 1]?.value
        : breadCrumbs[0]?.value;
    biService(
      {
        origin: breadCrumbs?.length === 1 ? 'home' : 'folder',
        results_count,
        search_text: searchValue?.value,
        folder_name,
      },
      BI_FILE_SHARE_WIDGET_SEARCH,
    );
  } catch (e) {
    const error: any = e;
    if (!isAbortError(error) && !signal.aborted) {
      setIsServerError({
        isError: true,
        message: error.message,
      });
      setIsLoading(false);
      result = error.message;
    }
  } finally {
    clearController();
  }
  return result;
};

export const getCurrentSelectedId = async () => {
  const tempResult = selectedFolderIdSubject$.asObservable();
  const lastTempResult = tempResult.pipe(take(1));
  const previousResult = await lastValueFrom(lastTempResult);
  return previousResult;
};

const getSearchValue = async () => {
  const tempResult = getSearchSubject().asObservable();
  const lastTempResult = tempResult.pipe(take(1));
  const previousResult = await lastValueFrom(lastTempResult);
  return previousResult;
};

const getSortValue = async () => {
  const tempResult = getSort().asObservable();
  const lastTempResult = tempResult.pipe(take(1));
  const previousResult = await lastValueFrom(lastTempResult);
  return previousResult;
};
export const getSelectedFolder = async () => {
  const tempResult = getSelectedFolder$().asObservable();
  const lastTempResult = tempResult.pipe(take(1));
  const previousResult = await lastValueFrom(lastTempResult);
  return previousResult;
};
const getBreadCrumbsValue = async () => {
  const tempResult = getBreadCrumbs$().asObservable();
  const lastTempResult = tempResult.pipe(take(1));
  const previousResult = await lastValueFrom(lastTempResult);
  return previousResult;
};

export const resetSearch = async () => {
  setSearchStatus(false);
  setSearchSubject({ callApi: true, value: '' });
  setDisableButtonsStatus(false);
};

export const navigateToParentFolder = async (item: any) => {
  try {
    setIsLoading(true);
    const tempResult = getSelectedFolderId().asObservable();
    const lastTempResult = tempResult.pipe(take(1));
    const previousResult = await lastValueFrom(lastTempResult);
    const folderId =
      item.path.length > 0 ? item.path[item.path.length - 1].libraryItemId : '';

    const breadCrumbs = item.path.map(
      (folder: { name: string; libraryItemId: string }) => {
        return {
          value: folder.name,
          id: folder.libraryItemId,
        };
      },
    );
    setBreadCrumbs(breadCrumbs);
    setSearchSubject({ callApi: false, value: '' });
    setDisableButtonsStatus(false);
    if (folderId === previousResult) {
      setSelectedFolder(folderId);
    } else {
      setSelectedFolderId(folderId);
    }
  } catch (e) {}
};
